// Screen-space picking. World-space circles misalign with projected 2.5D sprites.
import { worldToScreen } from '../core/coords';
import type { CameraView, Vec2 } from '../core/coords';
import { FLYER_HOVER_LEVELS } from '../core/projection';
import type { Registry } from '../data/registry';
import type { NavGrid } from '../sim/nav-grid';
import { isVisibleTo } from '../sim/views';
import { garrisonedInId } from '../sim/capabilities';
import type { GameState, Entity, EntityId, PlayerId } from '../sim/types';

function entityPickHeight(e: Entity, nav: NavGrid | null, registry?: Registry): number {
  const base = nav ? nav.surfaceHeightAtWorld(e.pos.x, e.pos.y) : 0;
  const flying = e.kind === 'unit' && registry?.units.get(e.defId)?.mobility === 'air';
  return base + (flying ? FLYER_HOVER_LEVELS : 0);
}

function screenPickRadius(e: Entity, cam: CameraView): number {
  const base =
    e.kind === 'unit' ? 32 : e.kind === 'building' ? 36 : e.kind === 'resource_node' ? 38 : 28;
  return base * cam.zoom;
}

export function pickResourceNodeScreen(
  state: GameState,
  viewerId: PlayerId,
  screen: Vec2,
  cam: CameraView,
  nav: NavGrid | null,
  registry?: Registry,
): Entity | null {
  let best: Entity | null = null;
  let bestD = Infinity;
  for (const e of state.entities.values()) {
    if (e.kind !== 'resource_node' || (e.amount ?? 0) <= 0) continue;
    if (nav && !isVisibleTo(state, viewerId, e, nav)) continue;
    const s = worldToScreen(e.pos, cam, entityPickHeight(e, nav, registry));
    const dx = screen.x - s.x;
    const dy = screen.y - s.y;
    const r = screenPickRadius(e, cam);
    const d2 = dx * dx + dy * dy;
    if (d2 <= r * r && d2 < bestD) {
      bestD = d2;
      best = e;
    }
  }
  return best;
}

export function pickEntityScreen(
  state: GameState,
  viewerId: PlayerId,
  screen: Vec2,
  cam: CameraView,
  nav: NavGrid | null,
  registry?: Registry,
): Entity | null {
  let best: Entity | null = null;
  let bestScore = -Infinity;
  for (const e of state.entities.values()) {
    if (e.kind === 'projectile') continue;
    if (e.kind === 'unit' && garrisonedInId(e) !== undefined) continue;
    if (nav && !isVisibleTo(state, viewerId, e, nav)) continue;
    const s = worldToScreen(e.pos, cam, entityPickHeight(e, nav, registry));
    const dx = screen.x - s.x;
    const dy = screen.y - s.y;
    const r = screenPickRadius(e, cam);
    if (dx * dx + dy * dy <= r * r) {
      const score = (e.kind === 'unit' ? 100 : e.kind === 'building' ? 50 : 10) - (dx * dx + dy * dy) / 1000;
      if (score > bestScore) {
        bestScore = score;
        best = e;
      }
    }
  }
  return best;
}

export function pickResourceNodeForInput(
  state: GameState,
  viewerId: PlayerId,
  _world: Vec2,
  screen: Vec2,
  cam: CameraView,
  nav: NavGrid | null,
  registry?: Registry,
): Entity | null {
  return pickResourceNodeScreen(state, viewerId, screen, cam, nav, registry);
}

export function pickEntityForInput(
  state: GameState,
  viewerId: PlayerId,
  _world: Vec2,
  screen: Vec2,
  cam: CameraView,
  nav: NavGrid | null,
  registry?: Registry,
): Entity | null {
  return pickEntityScreen(state, viewerId, screen, cam, nav, registry);
}

/** Units whose projected screen position falls inside a screen-space drag rectangle. */
export function unitsInScreenBox(
  state: GameState,
  ownerId: PlayerId,
  a: Vec2,
  b: Vec2,
  cam: CameraView,
  nav?: NavGrid | null,
  registry?: Registry,
): EntityId[] {
  const minSX = Math.min(a.x, b.x);
  const maxSX = Math.max(a.x, b.x);
  const minSY = Math.min(a.y, b.y);
  const maxSY = Math.max(a.y, b.y);
  const units: EntityId[] = [];
  for (const e of state.entities.values()) {
    if (e.owner !== ownerId || e.kind !== 'unit') continue;
    if (garrisonedInId(e) !== undefined) continue;
    const s = worldToScreen(e.pos, cam, entityPickHeight(e, nav ?? null, registry));
    if (s.x >= minSX && s.x <= maxSX && s.y >= minSY && s.y <= maxSY) units.push(e.id);
  }
  return units;
}
